import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Lock, Mail, AlertCircle } from "lucide-react";
import Logo from "@/components/Logo";

const DEMO_ACCOUNTS = [
  { label: "Doctor", email: "doctor@chemo.app", password: "Doctor@123" },
  { label: "Admin", email: "admin@chemo.app", password: "Admin@123" },
  { label: "Nurse", email: "nurse@chemo.app", password: "Nurse@123" },
  { label: "Patient", email: "patient@chemo.app", password: "Patient@123" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.password_must_change) {
        nav("/profile?force=password", { replace: true, state: { pwdReset: true } });
      } else {
        nav(u.role === "patient" ? "/m" : "/dashboard", { replace: true });
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const fillDemo = (a) => { setEmail(a.email); setPassword(a.password); };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-stone-50">
      {/* Left: hero panel */}
      <div className="hidden lg:flex relative overflow-hidden hero-gradient">
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="text-white">
            <Logo size={40} withWordmark subtitle="Chemotherapy Treatment Companion" />
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
              Treatment scheduling, finally in rhythm.
            </h1>
            <p className="mt-4 text-brand-100 text-lg leading-relaxed">
              Build chemotherapy regimens, generate patient calendars in seconds, and keep providers and patients in sync — every cycle, every dose, every visit.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
              <div><div className="text-3xl font-display font-bold">187</div><div className="text-brand-200 mt-1">Oncology drugs</div></div>
              <div><div className="text-3xl font-display font-bold">4+</div><div className="text-brand-200 mt-1">Standard regimens</div></div>
              <div><div className="text-3xl font-display font-bold">24/7</div><div className="text-brand-200 mt-1">Patient access</div></div>
            </div>
          </div>
          <div className="text-xs text-brand-200">© OncoCadence — secure provider &amp; patient portal</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-brand-700">
            <Logo size={36} withWordmark />
          </div>
          <h2 className="font-display text-3xl font-bold text-stone-900">Welcome back</h2>
          <p className="text-stone-500 mt-2 text-xs uppercase tracking-[0.18em] font-semibold">Chemotherapy Treatment Companion</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  data-testid="login-email-input"
                  type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-stone-300 bg-white text-stone-900 placeholder-stone-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  data-testid="login-password-input"
                  type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-stone-300 bg-white text-stone-900 placeholder-stone-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  placeholder="••••••••"
                />
              </div>
            </div>
            {err && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4 shrink-0" />{err}
              </div>
            )}
            <button
              data-testid="login-submit-button"
              type="submit" disabled={busy}
              className="w-full py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold transition-colors disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-sm text-stone-500 text-center space-y-1.5">
            <div>
              Don't have an account?{" "}
              <Link to="/register" className="text-brand-600 hover:text-brand-700 font-semibold" data-testid="register-link">
                Register
              </Link>
            </div>
            <div>
              Forgot your password?{" "}
              <Link to="/reset-password" className="text-brand-600 hover:text-brand-700 font-semibold" data-testid="forgot-password-link">
                Reset it here
              </Link>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-stone-200">
            <div className="text-xs font-medium uppercase tracking-wider text-stone-500 mb-3">Demo accounts</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ACCOUNTS.map((a) => (
                <button
                  key={a.email}
                  type="button"
                  onClick={() => fillDemo(a)}
                  data-testid={`demo-${a.label.toLowerCase()}-btn`}
                  className="px-3 py-2 rounded-lg border border-stone-200 hover:border-brand-300 hover:bg-brand-50 text-left transition-colors"
                >
                  <div className="text-sm font-semibold text-stone-800">{a.label}</div>
                  <div className="text-xs text-stone-500 truncate">{a.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
